<template>
  <div class="margin-subpages">
    <development-person v-if="personKey" :email-key="personKey" @back="closePerson"></development-person>
    <template v-else>
      <div class="flex-row-between mt-24">
        <el-select v-model="selectTeam" filterable :placeholder="$t('skills.development.select_placeholder')"
          class="input-style" :class="[ blurMode ? 'blurelement' : '' ]" @change="page = 1">
          <el-option v-if="user.user_type !== 'leadership'" class="text-dropdown" key="all"
            :label="`${$t('skills.development.all_teams')} (${$utils('addCommasInNumber', optionsTeam.length)})`"
            value="all"></el-option>
          <el-option class="text-dropdown" :class="[ blurMode ? 'blurelement' : '' ]"
            v-for="item in optionsTeam" :key="item.key" :label="item.name" :value="item.key"></el-option>
        </el-select>
        <div style="width: 240px" id="menuDropdownType" class="flex">
          <el-menu class="el-menu-demo menu-option-style" mode="horizontal">
            <el-submenu index="0" style="width: 240px">
              <template slot="title">
                <span class="txtOption">{{ periodLabelFor(periodLabel) }}</span>
              </template>
              <el-menu-item v-for="p in periods" :key="p" :index="p" @click="changePeriod(p)"
                style="font-size: 14px; width: 240px">{{ periodLabelFor(p) }}</el-menu-item>
            </el-submenu>
          </el-menu>
        </div>
      </div>

      <div class="dev-toolbar">
        <span class="dev-meta">{{ $utils('addCommasInNumber', filteredRows.length) }} {{ $t('skills.development.members_label') }}</span>
        <el-input v-model="search" class="dev-search" prefix-icon="el-icon-search"
          :placeholder="$t('skills.development.search')" clearable @input="page = 1"></el-input>
      </div>

      <div v-if="loading" class="icon-buttom-loading flex-mid">
        <loading-even></loading-even>
      </div>
      <template v-else>
        <el-table id="developmentTable" :data="pagedRows" :empty-text="$t('skills.development.no_members')"
          @sort-change="sortRows" @row-click="openPerson" row-class-name="dev-row">
          <el-table-column prop="name" sortable="custom" :label="$t('skills.development.member')" min-width="200">
            <template slot-scope="scope">
              <div class="dev-member">
                <img v-if="scope.row.profilepic" v-lazy="scope.row.profilepic" :key="scope.row.profilepic"
                  class="image-skeleton image-profile-42 flex-mid" :class="[ blurMode ? 'blurelement' : '' ]" />
                <div v-else class="image image-profile-42 flex-mid" :class="[ blurMode ? 'blurelement' : '' ]">
                  <div class="text-in-image text-in-image-42 flex-mid">
                    {{ $utils("acronymName", { firstName: scope.row.firstname, lastName: scope.row.lastname }) }}
                  </div>
                </div>
                <div class="dev-member-text">
                  <span class="dev-name" :class="[ blurMode ? 'blurelement' : '' ]">{{ scope.row.firstname }} {{ scope.row.lastname }}</span>
                  <span class="dev-team" :class="[ blurMode ? 'blurelement' : '' ]">{{ scope.row.teamname }}</span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="ratingavg" sortable="custom" :label="$t('skills.development.rating_trend')" width="110" align="center">
            <template slot-scope="scope">
              <template v-if="scope.row.ratingavg !== null">
                <span class="dev-rating">{{ starsFor(ratingLevel(scope.row.ratingavg)) }}</span>
                <span v-if="scope.row.ratingDelta" class="dev-delta"
                  :style="`color: ${scope.row.ratingDelta > 0 ? '#1AC362' : '#F44336'};`">
                  {{ scope.row.ratingDelta > 0 ? '▲' : '▼' }}
                </span>
              </template>
              <span v-else class="dev-empty-cell">—</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('skills.development.top_strength')" min-width="130">
            <template slot-scope="scope">
              <span v-if="scope.row.topStrength" class="dev-chip is-str">{{ chipLabel(scope.row.topStrength) }}</span>
              <span v-else class="dev-empty-cell">—</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('skills.development.development_area')" min-width="130">
            <template slot-scope="scope">
              <span v-if="scope.row.topImprove" class="dev-chip is-dev">{{ chipLabel(scope.row.topImprove) }}</span>
              <span v-else class="dev-empty-cell">—</span>
            </template>
          </el-table-column>
          <el-table-column prop="feedbackEvents" sortable="custom" :label="$t('skills.development.evidence')" min-width="156">
            <template slot-scope="scope">
              <template v-if="scope.row.feedbackEvents > 0">
                <div class="dev-ev-main">{{ mentionsLabel(scope.row.feedbackEvents) }} · {{ peersLabel(scope.row.giverCount) }}</div>
                <div v-if="scope.row.managerReviews > 0" class="dev-ev-sub has-mgr">
                  <span class="dev-mgr-dot"></span>{{ mgrReviewsLabel(scope.row.managerReviews) }}
                </div>
                <div v-else class="dev-ev-sub no-mgr">{{ $t('skills.development.no_manager_reviews_short') }}</div>
              </template>
              <div v-else-if="scope.row.managerReviews > 0" class="dev-ev-main dev-ev-mgr-only">
                <span class="dev-mgr-dot"></span>{{ mgrReviewsLabel(scope.row.managerReviews) }}
              </div>
              <span v-else class="dev-ev-empty">{{ $t('skills.development.no_feedback_yet') }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('skills.development.summary')" width="120" align="center">
            <template slot-scope="scope">
              <span class="dev-status" :class="'is-' + scope.row.summaryStatus">
                {{ $t('skills.development.' + summaryKey(scope.row.summaryStatus)) }}<template v-if="scope.row.summaryStatus === 'needs_data'"> ({{ scope.row.feedbackEvents }}/{{ minFeedback }})</template>
              </span>
            </template>
          </el-table-column>
        </el-table>
        <div class="dev-hint">{{ $t('skills.development.open_hint') }}</div>
        <div v-if="filteredRows.length > pageSize" class="flex-center pagination mt-10">
          <el-pagination @current-change="changePage" :current-page="page" :page-size="pageSize"
            :pager-count="5" layout="prev, pager, next" :total="filteredRows.length"></el-pagination>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { resolveTeamScope } from "@/store/utils/teamScope";
import { PERIOD_LABEL_KEYS, starsFor, ratingLevel, MIN_FEEDBACK_EVENTS } from "@/utils/developmentPicture";

export default {
  name: "development",
  components: {
    loadingEven: require("../even/loading").default,
    developmentPerson: require("./development-person.vue").default,
  },
  data() {
    return {
      selectTeam: "",
      search: "",
      page: 1,
      pageSize: 10,
      sort: { prop: null, order: null },
      personKey: null,
      periods: ["Current month", "Last month", "Past 3-months", "Past 6-months", "Past year", "All-time"],
    };
  },
  computed: {
    // Minimum in-window mentions needed to generate a summary; drives the
    // "Needs data (have/need)" gap shown on blocked rows.
    minFeedback() {
      return MIN_FEEDBACK_EVENTS;
    },
    user() {
      return this.$store.getters["authen/user"];
    },
    blurMode() {
      return this.$store.getters["demomode/blurMode"];
    },
    optionsTeam() {
      return this.$store.getters["team/teamsfixedall"];
    },
    loading() {
      return this.$store.getters["development/loading"];
    },
    isLeadership() {
      return this.user && this.user.user_type === "leadership";
    },
    rows() {
      return this.$store.getters["development/rows"];
    },
    periodLabel() {
      return this.$store.getters["development/period"];
    },
    activeTeamKey() {
      // leadership stays scoped to their subtree; the || fallback covers the
      // pre-init window before the optionsTeam watcher sets selectTeam
      if (this.user && this.user.user_type === "leadership" && this.user.team) {
        return this.selectTeam || this.user.team;
      }
      return this.selectTeam;
    },
    teamScope() {
      if (this.activeTeamKey === "all" || this.activeTeamKey === "") return null;
      return resolveTeamScope(
        this.activeTeamKey,
        this.$store.getters["skillOverview/organization1level"],
        this.$store.getters["main/allTeam"]
      );
    },
    scopedRows() {
      if (!this.teamScope) return this.rows;
      return this.rows.filter(row => this.teamScope[row.team]);
    },
    filteredRows() {
      let rows = this.scopedRows;
      const query = this.search.trim().toLowerCase();
      if (query) {
        rows = rows.filter(row =>
          `${row.firstname || ""} ${row.lastname || ""}`.toLowerCase().includes(query) ||
          (row.email || "").toLowerCase().includes(query) ||
          (row.teamname || "").toLowerCase().includes(query)
        );
      }
      const { prop, order } = this.sort;
      if (prop && order) {
        const direction = order === "ascending" ? 1 : -1;
        rows = [...rows].sort((a, b) => {
          if (prop === "name") {
            const nameA = `${a.firstname || ""} ${a.lastname || ""}`.toLowerCase();
            const nameB = `${b.firstname || ""} ${b.lastname || ""}`.toLowerCase();
            return nameA < nameB ? -direction : nameA > nameB ? direction : 0;
          }
          // "—" cells always sort last in both directions (proficiencies idiom)
          const aNull = a[prop] === null || a[prop] === undefined;
          const bNull = b[prop] === null || b[prop] === undefined;
          if (aNull && bNull) return 0;
          if (aNull) return 1;
          if (bNull) return -1;
          return (a[prop] - b[prop]) * direction;
        });
      }
      return rows;
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
  },
  watch: {
    optionsTeam() {
      if (this.optionsTeam.length > 0 && this.selectTeam === "") {
        this.selectTeam = this.user.user_type == "leadership" ? this.user.team : "all";
      }
    },
    loading() {
      this.validateDeepLink();
    },
  },
  mounted() {
    if (this.optionsTeam.length === 0) {
      this.$store.dispatch("team/getTeamsOptionsFixed", this.user);
    }
    const organization1level = this.$store.getters["skillOverview/organization1level"] || {};
    if (Object.keys(organization1level).length === 0) {
      this.$store.dispatch("skillOverview/getOrganization1level");
    }
    if (this.optionsTeam.length > 0 && this.selectTeam === "") {
      this.selectTeam = this.user.user_type == "leadership" ? this.user.team : "all";
    }
    this.$store.dispatch("development/getEvidence", this.periodLabel);
    const deepLink = this.$route.query.person;
    if (typeof deepLink === "string" && deepLink) this.personKey = deepLink;
    this.validateDeepLink();
  },
  methods: {
    changePeriod(p) {
      this.page = 1;
      this.$store.dispatch("development/getEvidence", p);
    },
    // a hand-typed ?person= must not widen leadership visibility beyond
    // their subtree — validate the deep link once evidence has loaded
    validateDeepLink() {
      if (!this.personKey || !this.isLeadership || this.loading) return;
      if (!this.scopedRows.some(r => r.emailKey === this.personKey)) {
        this.closePerson();
      }
    },
    periodLabelFor(p) {
      return PERIOD_LABEL_KEYS[p] ? this.$t("skills.proficiencies." + PERIOD_LABEL_KEYS[p]) : p;
    },
    changePage(val) {
      this.page = val;
    },
    sortRows({ prop, order }) {
      this.sort = { prop, order };
      this.page = 1;
    },
    summaryKey(status) {
      return status === "fresh" ? "fresh" : status === "stale" ? "stale" : status === "needs_data" ? "needs_data" : "not_generated";
    },
    // manual $t interpolation — the app's $t has no interpolation engine
    tf(key, repl) {
      let s = String(this.$t("skills.development." + key));
      if (repl) Object.keys(repl).forEach(k => { s = s.split("{" + k + "}").join(String(repl[k])); });
      return s;
    },
    // chips drop the ×count when it's a lone mention — a single ×1 reads as
    // definitive; the count only earns its place from 2 upward.
    chipLabel(m) {
      return m.count >= 2 ? `${m.name} ×${m.count}` : m.name;
    },
    // Evidence column labels (feedback volume + distinct peers + manager reviews)
    mentionsLabel(n) {
      return this.tf(n === 1 ? "mention_count" : "mentions_count", { n });
    },
    peersLabel(n) {
      return this.tf(n === 1 ? "peer_count" : "peers_count", { n });
    },
    mgrReviewsLabel(n) {
      return this.tf(n === 1 ? "manager_review_count" : "manager_reviews_count", { n });
    },
    // ordinal 1-4 display (spec decision 9) — never decimal averages
    starsFor,
    ratingLevel,
    openPerson(row) {
      this.personKey = row.emailKey;
      this.$router.replace({ query: { ...this.$route.query, person: row.emailKey } }).catch(() => {});
    },
    closePerson() {
      this.personKey = null;
      const query = { ...this.$route.query };
      delete query.person;
      this.$router.replace({ query }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.dev-toolbar { display: flex; align-items: center; justify-content: space-between; margin-top: 24px; margin-bottom: 16px; }
.dev-meta { font-size: 12px; color: #8a8a8a; }
.dev-search { width: 240px; }
/* match the app's polished 8px inputs so search lines up under the period pill */
.dev-search >>> .el-input__inner { height: 40px; border-radius: 8px; border-color: #e2e4ea; }
/* period-preset pill — Proficiencies keeps these class styles scoped, so they
   never reach this component; mirror them here (rounded gray, no menu
   border-bottom bleed, label left / caret pushed to the right edge). */
.menu-option-style { border-bottom: transparent; background: #f4f4f4; border-radius: 4px; }
.txtOption { color: #4a4a4a; font-size: 14px; margin-left: 16px; width: 170px; display: inline-block; }
.dev-member { display: flex; align-items: center; gap: 10px; }
.dev-member-text { display: flex; flex-direction: column; }
.dev-name { font-size: 14px; font-weight: 600; color: #262626; }
.dev-team { font-size: 12px; color: #8a8a8a; }
.dev-rating { font-size: 14px; font-weight: 700; color: #262626; }
.dev-delta { font-size: 12px; font-weight: 600; margin-left: 6px; }
.dev-chip { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 10px; }
.dev-chip.is-str { background: #e5f7ee; color: #1d8a55; }
.dev-chip.is-dev { background: #fdf3dc; color: #a4761b; }
.dev-ev-main { font-size: 13px; font-weight: 600; color: #262626; }
.dev-ev-sub { font-size: 12px; margin-top: 2px; display: flex; align-items: center; gap: 5px; }
.dev-ev-sub.has-mgr { color: #a4761b; }
.dev-ev-sub.no-mgr { color: #b8b8c2; }
/* peer feedback absent but manager reviews present — promote them to the main line */
.dev-ev-mgr-only { color: #a4761b; display: flex; align-items: center; gap: 5px; }
.dev-mgr-dot { width: 8px; height: 8px; background: #F7B924; transform: rotate(45deg); border-radius: 2px; display: inline-block; flex: none; }
.dev-ev-empty { font-size: 12px; color: #b8b8c2; }
.dev-status { font-size: 12px; font-weight: 600; }
.dev-status.is-fresh { color: #1d8a55; }
.dev-status.is-stale { color: #a4761b; }
.dev-status.is-needs_data, .dev-status.is-not_generated { color: #8a8a8a; }
.dev-empty-cell { color: #bfbfbf; }
.dev-hint { font-size: 12px; color: #8a8a8a; padding: 10px 4px 0; }
#developmentTable >>> .dev-row { cursor: pointer; }
/* Wrap multi-word headers at spaces, not mid-word. The app-level
   `word-break: break-all` otherwise chops "RATING TREND" into "RATING TRE / ND"
   now that the columns are narrower. */
#developmentTable >>> .el-table__header-wrapper .cell { word-break: normal; }
</style>

<!-- el-submenu internals aren't reachable through scoped styles; mirror the
     global rule Proficiencies defines so the period pill renders here even if
     that tab hasn't been visited. -->
<style>
#menuDropdownType .el-submenu__title { height: 40px; line-height: 40px; background-color: #f4f4f4; }
</style>
